import { isLeFlightSavedItemData } from 'tripPlanner/api/types'
import { getMultiCityGroupId, getMultiCityLegOrder } from '../utils/multiCityFlightHelpers'
import type { CartPanelFlightItem, CartPanelItem } from './cartPanelItemTypes'

/**
 * Multi-city flights are persisted as one trip item per leg (separate
 * tripItemIds sharing a `flightGroupId`), because the flight-cart backend only
 * models 1- or 2-leg cart units. The cart UI, however, should present each
 * multi-city itinerary as a single "flight" — one tile, one combined price, one
 * set of actions. This module collapses the per-leg `CartPanelFlightItem`s into
 * a single grouped item (carrying the legs in `multiCityLegs`) so totals,
 * checkout-readiness, and rendering all treat the itinerary as one.
 */

function isMultiCityLeg(item: CartPanelFlightItem): boolean {
  return !!getMultiCityGroupId(item)
}

/**
 * Aggregate leg statuses into one. The group is only `active` when every leg is
 * active; otherwise the most actionable status wins so the tile prompts the user
 * to resolve the group (offer needed > fare/selection needed > hydrating).
 */
function aggregateCartStatus(legs: Array<CartPanelFlightItem>): CartPanelFlightItem['cartStatus'] {
  if (legs.every(leg => leg.cartStatus === 'active')) return 'active'
  if (legs.some(leg => leg.cartStatus === 'needs_flight_offer')) return 'needs_flight_offer'
  if (legs.some(leg => leg.cartStatus === 'needs_flight_selection')) return 'needs_flight_selection'
  return 'pending_hydration'
}

// All-or-nothing: a group total is only meaningful once every leg has a price.
// Summing a subset would show a confident-looking total that silently omits a
// leg's fare, so return undefined (the tile falls back to a loading state)
// until all legs resolve.
function sumDefined(values: Array<number | undefined>): number | undefined {
  const present = values.filter((value): value is number => value !== undefined)
  if (present.length === 0 || present.length !== values.length) return undefined
  return present.reduce((total, value) => total + value, 0)
}

function getGroupPrice(
  legs: Array<CartPanelFlightItem>,
  cartStatus: CartPanelFlightItem['cartStatus'],
): number | undefined {
  // Once active, the live per-leg cart prices are authoritative, so sum them.
  if (cartStatus === 'active') {
    return sumDefined(legs.map(leg => leg.pricing.price))
  }

  // While pending, prefer the persisted combined total (avoids drift from
  // re-summing rounded per-leg estimates); fall back to summing leg estimates.
  const firstSaved = legs[0]?.savedItemData
  const groupTotal = isLeFlightSavedItemData(firstSaved) ? firstSaved.flightGroupTotalPrice : undefined
  return groupTotal ?? sumDefined(legs.map(leg => leg.pricing.price))
}

function getGroupMemberPrice(legs: Array<CartPanelFlightItem>): number | undefined {
  // No persisted member group total exists, so sum per-leg member fares in every
  // state (including pending). sumDefined yields undefined when any leg lacks a
  // member price (a non-LuxPlus or mixed group), so those groups correctly fall
  // back to the public total.
  return sumDefined(legs.map(leg => leg.pricing.memberPrice))
}

function getGroupRoute(legs: Array<CartPanelFlightItem>): string {
  const airports: Array<string> = []
  legs.forEach((leg, index) => {
    const departure = leg.savedItemData.departureAirport
    const arrival = leg.savedItemData.arrivalAirport
    if (index === 0 && departure) airports.push(departure)
    if (arrival) airports.push(arrival)
  })
  return airports.join(' → ')
}

function buildGroupedItem(flightGroupId: string, rawLegs: Array<CartPanelFlightItem>): CartPanelFlightItem {
  const legs = [...rawLegs].sort((a, b) => getMultiCityLegOrder(a) - getMultiCityLegOrder(b))
  const [firstLeg] = legs
  const cartStatus = aggregateCartStatus(legs)
  const isAvailabilityBlocked = legs.some(leg => leg.isAvailabilityBlocked)

  return {
    ...firstLeg,
    key: `mc-group-${flightGroupId}`,
    cartStatus,
    cartExcluded: legs.every(leg => leg.cartExcluded),
    name: getGroupRoute(legs) || firstLeg.name,
    pricing: {
      price: getGroupPrice(legs, cartStatus),
      memberPrice: getGroupMemberPrice(legs),
      value: undefined,
      isAvailable: !isAvailabilityBlocked,
      loading: legs.some(leg => leg.pricing.loading),
      error: legs.some(leg => leg.pricing.error),
    },
    includeInTotal: cartStatus === 'active' && !isAvailabilityBlocked,
    // The group has no single backing cart item; the legs each keep their own.
    cartItemId: undefined,
    cartItem: undefined,
    flightCartItem: undefined,
    returnOption: undefined,
    returnSavedItemData: undefined,
    returnTripItemId: undefined,
    pairedTripItemIds: legs.flatMap(leg => leg.pairedTripItemIds),
    isAvailabilityBlocked,
    hasRefreshFailed: legs.some(leg => leg.hasRefreshFailed),
    multiCityLegs: legs,
  }
}

/**
 * Collapse multi-city legs into one grouped item per `flightGroupId`, preserving
 * the original ordering (the group takes the position of its first leg). Non
 * multi-city items pass through untouched.
 */
export function groupMultiCityFlightItems(
  items: Array<CartPanelItem>,
): Array<CartPanelItem> {
  const groups = new Map<string, Array<CartPanelFlightItem>>()
  const order: Array<{ type: 'group'; id: string } | { type: 'item'; item: CartPanelItem }> = []

  for (const item of items) {
    if (item.productType === 'flight' && isMultiCityLeg(item)) {
      const groupId = getMultiCityGroupId(item)
      if (groupId) {
        if (!groups.has(groupId)) {
          groups.set(groupId, [])
          order.push({ type: 'group', id: groupId })
        }
        groups.get(groupId)!.push(item)
        continue
      }
    }
    order.push({ type: 'item', item })
  }

  return order.map(entry => {
    if (entry.type === 'item') return entry.item
    return buildGroupedItem(entry.id, groups.get(entry.id) ?? [])
  })
}
